/**
 * Position Store User Context Hook
 * 
 * This hook manages the user context for the position store,
 * ensuring that position data is properly isolated per user.
 */

import { useEffect, useRef } from 'react';
import { usePositionStore } from '../store/positionStore';
import { useUserContext } from './useUserContext';
import { 
  savePositionStoreData, 
  loadPositionStoreData, 
  switchUserContext,
  clearUserPositionData,
  validatePositionStoreData 
} from '../utils/positionStorePersistence';

interface UsePositionStoreUserContextOptions {
  enabled?: boolean;
  autoSwitchUser?: boolean;
  validateOnLoad?: boolean;
}

/**
 * Hook to manage position store user context and persistence
 */
export const usePositionStoreUserContext = (options: UsePositionStoreUserContextOptions = {}) => {
  const { 
    enabled = true, 
    autoSwitchUser = true, 
    validateOnLoad = true 
  } = options;
  
  const { userId: currentUserId, isReady: isUserContextReady } = useUserContext();
  const previousUserIdRef = useRef<string | null>(null);
  
  const positionStore = usePositionStore();
  
  // Handle user context changes
  useEffect(() => {
    if (!enabled || !isUserContextReady) {
      return;
    }
    
    const previousUserId = previousUserIdRef.current;
    
    // User logged in or switched
    if (currentUserId && currentUserId !== previousUserId) {
      console.log(`🔐 [Position Store User Context] User transition: ${previousUserId?.substring(0, 20)}... → ${currentUserId.substring(0, 20)}...`);
      
      if (autoSwitchUser) {
        // Save current position store data before switching (if there was a previous user)
        if (previousUserId && positionStore.positions.size > 0) {
          const dataToSave = {
            positions: Array.from(positionStore.positions.entries()),
            isInitialized: positionStore.isInitialized,
            totalPositions: positionStore.totalPositions,
            totalUnrealizedPnl: positionStore.totalUnrealizedPnl,
            lastUpdate: positionStore.lastUpdate,
            lastSnapshotTime: positionStore.lastSnapshotTime,
            currentUserId: previousUserId
          };
          
          savePositionStoreData(previousUserId, dataToSave);
        }
        
        // Switch user context in position store
        positionStore.setUserContext(currentUserId);
        
        // Try to load data for new user
        const userData = loadPositionStoreData(currentUserId);
        if (userData && validateOnLoad && validatePositionStoreData(userData, currentUserId)) {
          console.log(`💾 [Position Store User Context] Loading saved data for user: ${currentUserId.substring(0, 20)}...`);
          
          // Convert Map values to array of Position objects for handlePositionSnapshot
          // userData.positions is Array<[key, position]>, so extract just the values
          const positionsArray = userData.positions.map(([_, position]) => position);
          
          // Use the proper Zustand method to load positions (unfreezes automatically)
          positionStore.handlePositionSnapshot({
            type: 'position_snapshot',
            data: {
              positions: positionsArray,
              timestamp: userData.lastSnapshotTime || Date.now(),
              user_id: currentUserId,
              total_count: userData.totalPositions,
              source: 'reconnection' // Use 'reconnection' as localStorage restore is similar
            }
          });
        } else {
          console.log(`📭 [Position Store User Context] No valid saved data for user: ${currentUserId.substring(0, 20)}...`);
          // Start fresh for this user
          positionStore.clearPositions();
        }
        
        // Handle persistence context switching
        switchUserContext(previousUserId, currentUserId);
      }
      
      previousUserIdRef.current = currentUserId;
    }
    
    // User logged out
    if (!currentUserId && previousUserId) {
      console.log(`🚪 [Position Store User Context] User logout detected`);
      
      if (autoSwitchUser) {
        // Save current data before clearing
        if (positionStore.positions.size > 0) {
          const dataToSave = {
            positions: Array.from(positionStore.positions.entries()),
            isInitialized: positionStore.isInitialized,
            totalPositions: positionStore.totalPositions,
            totalUnrealizedPnl: positionStore.totalUnrealizedPnl,
            lastUpdate: positionStore.lastUpdate,
            lastSnapshotTime: positionStore.lastSnapshotTime,
            currentUserId: previousUserId
          };
          
          savePositionStoreData(previousUserId, dataToSave);
        }
        
        // Clear position store
        positionStore.clearUserData();
      }
      
      previousUserIdRef.current = null;
    }
  }, [currentUserId, isUserContextReady, enabled, autoSwitchUser, validateOnLoad, positionStore]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Save current state on unmount if we have a user
      if (currentUserId && positionStore.positions.size > 0) {
        const dataToSave = {
          positions: Array.from(positionStore.positions.entries()),
          isInitialized: positionStore.isInitialized,
          totalPositions: positionStore.totalPositions,
          totalUnrealizedPnl: positionStore.totalUnrealizedPnl,
          lastUpdate: positionStore.lastUpdate,
          lastSnapshotTime: positionStore.lastSnapshotTime,
          currentUserId: currentUserId
        };
        
        savePositionStoreData(currentUserId, dataToSave);
        console.log(`💾 [Position Store User Context] Saved data on unmount for user: ${currentUserId.substring(0, 20)}...`);
      }
    };
  }, [currentUserId, positionStore]);
  
  return {
    currentUserId: positionStore.currentUserId,
    isUserContextReady,
    
    // Manual control functions
    switchToUser: (userId: string | null) => {
      positionStore.setUserContext(userId);
    },
    
    clearCurrentUserData: () => {
      if (currentUserId) {
        clearUserPositionData(currentUserId);
      }
      positionStore.clearUserData();
    },
    
    saveCurrentState: () => {
      if (currentUserId) {
        const dataToSave = {
          positions: Array.from(positionStore.positions.entries()),
          isInitialized: positionStore.isInitialized,
          totalPositions: positionStore.totalPositions,
          totalUnrealizedPnl: positionStore.totalUnrealizedPnl,
          lastUpdate: positionStore.lastUpdate,
          lastSnapshotTime: positionStore.lastSnapshotTime,
          currentUserId: currentUserId
        };
        
        savePositionStoreData(currentUserId, dataToSave);
        return true;
      }
      return false;
    },
    
    loadUserData: (userId: string) => {
      const userData = loadPositionStoreData(userId);
      if (userData && validatePositionStoreData(userData, userId)) {
        positionStore.setUserContext(userId);
        // Load the data... (implementation would go here)
        return true;
      }
      return false;
    }
  };
};